import { Script } from "gatsby"
import { Dispatch, FC, useEffect, useState } from "react"
import { AnyAction } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"

// utils
import { extractLangCode } from "../../utils/general_utils"

// types
import { RootState } from "../../store/store"
import { UserProfile } from "../../store/slices/user_profile.slice"
import { setMetaData } from "../../store/slices/metadata.slice"

type UserFormData = {
  last_name: string
  topic: string
  event_url: string
  description: string
  emailaddress: string
  organizationid: string
  locale: string
  language: string
}

type SingleCustomField = {
  ident: string
  value: string
  updatedAt: string
}

export const NiceCXOneScript: FC<{ dispatch: Dispatch<AnyAction> }> = ({
  dispatch
}) => {
  const chatChannelId = process.env.GATSBY_NICE_CHAT_CHANNEL_ID

  if (!chatChannelId) {
    console.warn("No chat channel id provided")

    return null
  }

  const userProfile = useSelector((store: RootState) => store.userProfile)
  const cxoneReady = useSelector(
    (store: RootState) => store.metaData.cxoneReady
  )
  const niceChatWindowOpened = useSelector(
    (store: RootState) => store.metaData.niceChatWindowOpened
  )
  const niceChatIsLive = useSelector(
    (store: RootState) => store.metaData.niceChatIsLive
  )
  const niceChatHasConcluded = useSelector(
    (store: RootState) => store.metaData.niceChatHasConcluded
  )

  const [caseId, setCaseId] = useState("")
  const [salesforceCaseId, setSalesforceCaseId] = useState("")
  const [chatHasStarted, setChatHasStarted] = useState(false)
  const [chatHasEnded, setChatHasEnded] = useState(false)
  const [userFormData, setUserFormData] = useState<
    UserFormData | Record<string, string>
  >({})
  const [niceCaseStatus, setNiceCaseStatus] = useState("")
  const [niceThreadsLength, setNiceThreadsLength] = useState(0)
  const [isAChatRestart, setIsAChatRestart] = useState(false)

  const langCode = extractLangCode()

  if (process.env.GATSBY_DEBUG === "true") {
    console.log("🚀 ~ NiceCXOneScript render: ", {
      isAChatRestart,
      niceCaseStatus,
      niceChatIsLive,
      niceChatHasConcluded,
      salesforceCaseId,
      caseId,
      chatHasStarted,
      chatHasEnded,
      langCode,
      cxoneReady,
      niceChatWindowOpened,
      userFormData
    })
  }

  const setUserProfileDataInNiceChat = (
    userProfile: UserProfile,
    langCode: string,
    userFormData: UserFormData | Record<string, string>
  ) => {
    if (window?.cxone) {
      if (process.env.GATSBY_DEBUG === "true") {
        console.log("🚀 ~ uProfile:", { userProfile, langCode })
      }

      if (userProfile.email) {
        window.cxone(
          "chat",
          "setCaseCustomField",
          "emailaddress",
          userProfile.email
        )
      }

      if (userProfile.organizationId) {
        window.cxone(
          "chat",
          "setCaseCustomField",
          "organizationid",
          userProfile.organizationId
        )
      }
      window.cxone("chat", "setCaseCustomField", "language", "English")
      window.cxone("chat", "setCaseCustomField", "locale", langCode)

      if (userFormData) {
        if (userFormData?.last_name) {
          window.cxone(
            "chat",
            "setCaseCustomField",
            "last_name",
            userFormData.last_name
          )
        }
        if (userFormData?.topic) {
          window.cxone(
            "chat",
            "setCaseCustomField",
            "topic",
            userFormData.topic
          )
        }
        if (userFormData?.event_url) {
          window.cxone(
            "chat",
            "setCaseCustomField",
            "event_url",
            userFormData.event_url
          )
        }
        if (userFormData?.description) {
          window.cxone(
            "chat",
            "setCaseCustomField",
            "description",
            userFormData.description
          )
        }
      }

      if (process.env.GATSBY_DEBUG === "true") {
        console.log("FINISHED RUNNING setUserProfileDataInNiceChat()", {
          userProfile,
          userFormData,
          langCode
        })
      }
    } else {
      console.error("No window.cxone is available to set user data: ", {
        userProfile
      })
    }
  }

  useEffect(() => {
    if (typeof window === "undefined") return

    const cxOneReadyInterval = setInterval(() => {
      if (typeof window?.cxone !== "undefined") {
        dispatch(
          setMetaData({
            cxoneReady: true
          })
        )
      }
    }, 250)

    if (cxoneReady) {
      clearInterval(cxOneReadyInterval)
    }

    const niceChatWindowOpenedInterval = setInterval(() => {
      const niceBEFrameContainer = document.querySelector(
        "#be-frame"
      ) as HTMLDivElement

      if (
        niceBEFrameContainer &&
        niceBEFrameContainer?.style &&
        niceBEFrameContainer?.style?.visibility === "hidden" &&
        niceChatWindowOpened === true
      ) {
        dispatch(
          setMetaData({
            niceChatWindowOpened: false
          })
        )
      }

      if (
        niceBEFrameContainer &&
        niceBEFrameContainer?.style &&
        niceBEFrameContainer?.style?.visibility === "visible" &&
        niceChatWindowOpened === false
      ) {
        dispatch(
          setMetaData({
            niceChatWindowOpened: true,
            shouldShowAIChatbot: false
          })
        )
      }
    }, 1000)

    return () => {
      clearInterval(cxOneReadyInterval)
      clearInterval(niceChatWindowOpenedInterval)
    }
  }, [cxoneReady, niceChatWindowOpened])

  useEffect(() => {
    if (!cxoneReady || typeof window === "undefined") return

    window.cxone?.(
      "chat",
      "onPushUpdate",
      ["CaseStatusChanged"],
      (event: any) => {
        if (
          event?.data?.case?.status &&
          typeof event?.data?.case?.status === "string"
        ) {
          setNiceCaseStatus(
            event?.data?.case?.status?.toLowerCase()?.trim()
          )
        }
      }
    )

    window.cxone?.("chat", "onAnyPushUpdate", (event: any) => {
      if (event?.data?.message?.messageContent?.text) {
        if (process.env.GATSBY_DEBUG === "true") {
          console.log("cxone MESSAGE: ", {
            message: event.data.message,
            text: event.data.message?.messageContent?.text
          })
        }

        if (
          event?.data?.message?.messageContent?.text
            ?.toLowerCase()
            ?.includes("case number")
        ) {
          const sfCaseId =
            event?.data?.message?.messageContent?.text?.match(
              /case number is\s*:\s*(\w+)/i
            )
          const caseIdMatch = sfCaseId ? sfCaseId[1] : null

          if (process.env.GATSBY_DEBUG === "true") {
            console.log("🚀 ~ window?.cxone ~ sfCaseId:", {
              sfCaseId,
              caseIdMatch
            })
          }

          if (caseIdMatch) {
            setSalesforceCaseId(caseIdMatch)
          }
        }
      }

      if (event?.data?.case) {
        // @ts-ignore
        const uData: UserFormData = {}

        if (event?.data?.case?.customFields?.length > 0) {
          event?.data?.case.customFields.forEach((y: SingleCustomField) => {
            if (y.value && y.ident) {
              // @ts-ignore
              uData[y.ident] = y.value
            }
          })

          if (uData?.last_name && uData?.event_url) {
            setUserFormData(uData)
          }
        }
      }
    })
  }, [cxoneReady])

  useEffect(() => {
    if (typeof window === "undefined") return

    const interval = setInterval(() => {
      const ongoingContact = window.cxone?.("chat", "getOngoingContact") as
        | { contact: { id: string } }
        | undefined

      if (ongoingContact?.contact?.id) {
        setCaseId(ongoingContact?.contact?.id)
      }
    }, 500)

    if (caseId) {
      clearInterval(interval)
    }

    return () => clearInterval(interval)
  }, [caseId])

  useEffect(() => {
    if (process.env.GATSBY_DEBUG === "true") {
      console.log("niceThreadsLength: ", {
        niceThreadsLength,
        niceCaseStatus
      })
    }

    if (
      niceThreadsLength <= 0 &&
      niceCaseStatus === "closed" &&
      chatHasEnded === true
    ) {
      setIsAChatRestart(true)
    }

    if (niceThreadsLength <= 0 && niceCaseStatus === "closed") {
      setChatHasEnded(true)

      dispatch(
        setMetaData({
          niceChatIsLive: false,
          niceChatHasConcluded: true
        })
      )
    }

    if (niceThreadsLength > 0 && niceCaseStatus !== "closed") {
      setChatHasStarted(true)

      dispatch(
        setMetaData({
          niceChatIsLive: true,
          niceChatHasConcluded: false,
        })
      )
    }
  }, [niceThreadsLength, niceCaseStatus, niceChatIsLive, chatHasEnded])

  useEffect(() => {
    if (process.env.GATSBY_DEBUG === "true") {
      console.log("🚀 ~ isAChatRestart:", {
        isAChatRestart,
        userProfile,
        langCode,
        userFormData
      })
    }

    if (isAChatRestart === true) {
      if (userProfile && langCode && userFormData) {
        setUserProfileDataInNiceChat(userProfile, langCode, userFormData)
      }

      setIsAChatRestart(false)
    }
  }, [isAChatRestart, userProfile, langCode, userFormData])

  useEffect(() => {
    if (chatHasEnded) {
      dispatch(
        setMetaData({
          niceChatHasConcluded: true
        })
      )
    }

    if (typeof window !== "undefined" && chatHasEnded && window?.usabilla_live) {
      if (salesforceCaseId) {
        if (process.env.GATSBY_DEBUG === "true") {
          console.log(
            "salesforceCaseId is being assigned: ",
            salesforceCaseId
          )
        }
        window?.usabilla_live("trigger", "WebChatService.ended")

        window?.usabilla_live("data", {
          custom: {
            CaseId: salesforceCaseId
          }
        })
      }
    }
  }, [chatHasEnded, salesforceCaseId])

  useEffect(() => {
    if (typeof window === "undefined" || chatHasStarted) return

    const interval = setInterval(() => {
      const ongoingThreads = window.cxone?.("chat", "getOngoingThreads")

      if (ongoingThreads?.length >= 0) {
        setNiceThreadsLength(ongoingThreads.length)
      }
    }, 500)

    return () => clearInterval(interval)
  }, [chatHasStarted])

  useEffect(() => {
    if (typeof window === "undefined" || !chatHasStarted) return

    const interval = setInterval(() => {
      const ongoingThreads = window.cxone?.("chat", "getOngoingThreads")

      if (ongoingThreads?.length >= 0) {
        setNiceThreadsLength(ongoingThreads.length)
      }
    }, 500)

    if (chatHasEnded) {
      clearInterval(interval)
    }

    return () => clearInterval(interval)
  }, [chatHasStarted, chatHasEnded])

  useEffect(() => {
    if (typeof window === "undefined") return
    if (userProfile !== null && window?.cxone) {
      setUserProfileDataInNiceChat(userProfile, langCode, {})
    }
  }, [userProfile, langCode])

  useEffect(() => {
    if (typeof window === "undefined" || !niceChatHasConcluded) return

    const chatGuideElement = document.querySelector<HTMLDivElement>(
      "#cxone-guide-container"
    )

    if (typeof chatGuideElement?.style !== "undefined") {
      chatGuideElement.style.display = "none"
    }
  }, [niceChatHasConcluded])

  return (
    <>
      <Script id="NiceCxOneScript" type="application/javascript">
        {`
            (function(n, u) {
                window.CXoneDfo = n;
                window[n] = window[n] || function() {
                    (window[n].q = window[n].q || []).push(arguments);
                };
                window[n].u = u;
                
                let e = document.createElement("script"); // Explicitly declare e
                e.type = "module";
                e.src = u + "?" + Math.round(Date.now() / 1e3 / 3600);
                document.head.appendChild(e);
            })('cxone', 'https://web-modules-de-na1.niceincontact.com/loader/1/loader.js');

            cxone("chat", "setCustomerId", String(Math.round(Date.now() / 1e3 / 3600) + Math.random()))
            cxone("init", "5388")
            cxone("guide", "init", "${chatChannelId}")
            cxone("guide", "setButtonSize", "0px")
            cxone("guide", "setOffsetX", "48px")
            cxone("guide", "setOffsetY", "48px")

            cxone("chat", "enableCloseIcon", true)
          `}
      </Script>

      <style>
        {`.icon.svelte-vefknh {
            background-color: #cc3f24 !important;
            --bg-color: #cc3f24 !important;
          }`}
      </style>
    </>
  )
}
